/**
 * Storage cleanup — Sprint 20w follow-up.
 *
 * Очищает legacy localStorage ключи которые могут утекать между orgs
 * (multi-tenancy leak source). Запускается на каждом login.
 *
 * Категории keys:
 *
 * 1. AUTH legacy (Sprint 15 C.1 убрал Bearer fallback) — DROP:
 *    - jwt_token, jwt_refresh_token, authToken, auth_user
 *    Они в коде нигде не читаются больше, но могут оставаться в браузере.
 *
 * 2. PER-ORG global keys (B010 leak source) — DROP:
 *    - quoteCalc_regions, quoteCalc_currentRegion (заменены на _${orgId} suffix)
 *    - quoteCalc_categories, quoteCalc_categories_${region} (TODO: migrate to server)
 *    - quoteCalc_templates, quoteCalc_templates_${region} (TODO: migrate to server)
 *    - quoteCalc_globalStats, quoteCalc_categoryStats, quoteCalc_templateStats
 *      (статистика per-org — server-side dashboard)
 *
 * 3. UI state (KEEP):
 *    - crm_sidebar_collapsed
 *    - locale
 *    - quoteCalc_servicesViewMode
 *    - quoteCalc_*Expanded (toggles)
 *
 * 4. Dangerous local cache (DROP):
 *    - quoteCalc_autosave, quoteCalc_autosave_timestamp — full estimate JSON,
 *      может содержать клиентские PII; server-side autosave работает.
 *    - quoteCalc_lastQuote, quoteCalc_lastQuoteFileName — legacy "last opened"
 *    - quoteCalc_state — legacy session state cache
 *    - quoteCalc_catalogFolder, quoteCalc_saveFolder — file browser dirs (deprecated)
 *    - quoteCalc_currentMode — legacy
 *
 * @module crm-shell/storage-cleanup
 */

// B013 fix: regions migrate (не drop!) — preserve user's saved regions/categories
// при transition с global key на per-org. Migration runs once per (key, orgId).
const KEYS_TO_MIGRATE_PER_ORG = [
    'quoteCalc_regions',
    'quoteCalc_currentRegion'
];

const LEGACY_KEYS_TO_DROP = [
    // Auth legacy (Sprint 15 C.1)
    'jwt_token', 'jwt_refresh_token', 'authToken', 'auth_user',

    // Org-leaking globals (B010 root cause) — categories/templates/stats
    // не критичны (server-side replacement) → safely drop
    'quoteCalc_categories',
    'quoteCalc_templates',
    'quoteCalc_globalStats',
    'quoteCalc_categoryStats',
    'quoteCalc_templateStats',

    // Dangerous local cache (PII risk + server-side replacement exists)
    'quoteCalc_autosave',
    'quoteCalc_autosave_timestamp',
    'quoteCalc_lastQuote',
    'quoteCalc_lastQuoteFileName',
    'quoteCalc_state',
    'quoteCalc_catalogFolder',
    'quoteCalc_saveFolder',
    'quoteCalc_currentMode'
];

const PREFIX_TO_DROP = [
    // Per-region categories/templates — leak across orgs
    'quoteCalc_categories_',
    'quoteCalc_templates_'
];

/**
 * Удаляет legacy/dangerous localStorage keys + мигрирует regions per-org.
 *
 * B013 fix: regions/currentRegion ранее удалялись → superadmin терял saved
 * Magellania regions. Теперь они мигрируют в `quoteCalc_regions_${orgId}`
 * для текущего user'а ПЕРЕД удалением legacy key.
 *
 * @param {string} [orgId] — для миграции legacy → per-org. Если не передан,
 *                            миграция пропускается, drop делает прежнее.
 * @returns {{removed: number, migrated: number}}
 */
export function cleanupLegacyStorage(orgId) {
    let removed = 0;
    let migrated = 0;
    try {
        // B013: migrate legacy regions/currentRegion → per-org keys (если ещё нет)
        if (orgId) {
            for (const baseKey of KEYS_TO_MIGRATE_PER_ORG) {
                const legacy = localStorage.getItem(baseKey);
                if (legacy === null) continue;
                const perOrgKey = `${baseKey}_${orgId}`;
                // Не overwrite если per-org key уже существует (юзер мог их менять)
                if (localStorage.getItem(perOrgKey) === null) {
                    localStorage.setItem(perOrgKey, legacy);
                    migrated++;
                    console.log(`[storage-cleanup] migrated ${baseKey} → ${perOrgKey}`);
                }
                // После миграции safe удалить legacy
                localStorage.removeItem(baseKey);
                removed++;
            }
        }

        // Exact matches — drop без миграции
        for (const key of LEGACY_KEYS_TO_DROP) {
            if (localStorage.getItem(key) !== null) {
                localStorage.removeItem(key);
                removed++;
            }
        }
        // Prefix matches (categories/templates per region — server-side replacement есть)
        const allKeys = Object.keys(localStorage);
        for (const key of allKeys) {
            if (/^quoteCalc_(regions|currentRegion)_/.test(key)) continue;
            for (const prefix of PREFIX_TO_DROP) {
                if (key.startsWith(prefix)) {
                    localStorage.removeItem(key);
                    removed++;
                    break;
                }
            }
        }
        if (removed > 0 || migrated > 0) {
            console.log(`[storage-cleanup] migrated ${migrated}, removed ${removed} legacy keys`);
        }
    } catch (err) {
        console.warn('[storage-cleanup] failed:', err.message);
    }
    return { removed, migrated };
}

/**
 * Очищает per-org localStorage при logout (чтобы next user не увидел чужое).
 * Вызывать перед redirect на /login.html.
 */
export function clearOrgStorage(orgId) {
    if (!orgId) return;
    try {
        const allKeys = Object.keys(localStorage);
        let removed = 0;
        for (const key of allKeys) {
            if (key.endsWith(`_${orgId}`)) {
                localStorage.removeItem(key);
                removed++;
            }
        }
        if (removed > 0) console.log(`[storage-cleanup] cleared ${removed} keys for org ${orgId}`);
    } catch (_) {}
}
